import { ref } from 'vue';

import { WIDGET_ERROR_KEY } from '@support/widget-contracts';

import { WidgetApiError, request } from '@/api/client';

/**
 * Whether the contact API this frame reached knows about attachments.
 *
 * The frame can be newer than the API it talks to. The two are deployed by
 * separate workflows from one push, so the frame is ahead for a minute during
 * every release — and for as long as it takes somebody to notice when the API's
 * new revision fails to start and the previous one goes on serving. A frame
 * that could only speak the newest schema would then load no thread and send no
 * message, for every visitor, over a feature none of them was using.
 *
 * So the attachment-aware documents are tried first, and an API that answers
 * "that is not an operation I know" is spoken to in the documents it does know
 * from then on. What is lost is the attachments, not the conversation.
 *
 * Not re-probed: a page that met an older API stays text-only until it is
 * reloaded, which is the same moment it would pick up a newer frame.
 */
export const attachmentsAvailable = ref(true);

/**
 * The API's answer to an operation its schema does not have. The contract
 * reserves this key for exactly that, which is what makes it safe to act on.
 */
export const isUnknownOperation = (error: unknown): boolean =>
  error instanceof WidgetApiError && error.key === WIDGET_ERROR_KEY.REQUEST_INVALID;

/**
 * Send `documents.current`; against an API that does not know it, send
 * `documents.previous` instead, now and for the rest of the page's life.
 */
export const requestWithAttachments = async <T>(
  documents: { current: string; previous: string },
  variables: Record<string, unknown>,
  sessionToken: string,
): Promise<T> => {
  if (attachmentsAvailable.value) {
    try {
      return await request<T>(documents.current, variables, sessionToken);
    } catch (error) {
      if (!isUnknownOperation(error)) {
        throw error;
      }

      attachmentsAvailable.value = false;
    }
  }

  return await request<T>(documents.previous, variables, sessionToken);
};
